import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ClipboardCheck,
  CreditCard,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Pencil,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import metadataRaw from "@/config/router_metadata.json";
import logo from "@/assets/logo.svg";
import { Header } from "@/components/Header";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { getSupabaseFunctionHttpStatus } from "@/lib/supabase-function-errors";
import {
  getAssistantArtifactId,
  getWizardOsNavigation,
  getWizardQuestionProgress,
  getWizardReviewEditAffectedLabel,
  getWizardReviewContextSummary,
  isWizardExistingAccountError,
  WIZARD_EXISTING_ACCOUNT_LOGIN_MESSAGE,
  WIZARD_UNSUPPORTED_OUTCOME_DESCRIPTION,
  WIZARD_UNSUPPORTED_OUTCOME_PRIMARY_CTA,
  WIZARD_UNSUPPORTED_OUTCOME_SECONDARY_CTA,
  WIZARD_UNSUPPORTED_OUTCOME_TITLE,
  wizardAllowsCustomAnswer,
} from "@/lib/wizard";
import {
  createCustomRouterAnswer,
  CUSTOM_ROUTER_ANSWER_ID,
  getCustomRouterAnswerText,
  MAX_CUSTOM_ROUTER_ANSWER_LENGTH,
  type IndustryMetadata,
  type Questionnaire,
  type RouterAnswerMap,
  type RouterMetadata,
  type RouterQuestion,
} from "../../supabase/functions/_shared/gateRouter";

type ResolvedLens = { id: string; topic: string; name: string; author?: string | null };
type AccessStep = "email" | "confirm-email" | "password" | "name" | "checkout" | "verify-email" | "login";
type PaymentMode = "promo" | "card";
type ReviewEditMode = "dependent" | "single";
interface PendingReviewEdit {
  questionId: string;
  questionIndex: number;
  answerCount: number;
  affectedLabel: string;
}
interface WizardAccount {
  name: string;
  email: string;
  confirmEmail: string;
  password: string;
  promoCode: string;
  cardNumber: string;
  expiry: string;
  cvc: string;
  cardName: string;
}

interface WizardRun {
  run_id: string;
  wizard_version?: number;
  allow_custom_answer?: boolean;
  status: string;
  resume_expires_at: string;
  desired_outcome: string;
  industries: IndustryMetadata[];
  industry: IndustryMetadata | null;
  questionnaire: {
    questionnaire_id: string;
    title: string;
    sections: Array<{ id: string; label: string }>;
  } | null;
  questions: RouterQuestion[];
  answers: RouterAnswerMap;
  result: null | {
    complete: boolean;
    missing_question_ids: string[];
    primary_lens_topic_id: string | null;
    supporting_lens_topic_ids: string[];
    score_trace: {
      selected_answer_ids?: string[];
      scores?: Record<string, number>;
      tie_break_scores?: Record<string, number> | null;
      tie_break_question_ids?: string[];
      tie_break_applied?: boolean;
      default_used?: boolean;
      explanation?: string;
    } | null;
    answer_trace?: ReturnType<typeof buildPreviewAnswerTrace>;
    primary_lens?: ResolvedLens | null;
    supporting_lenses?: ResolvedLens[];
    context_summary?: string;
    user_explanation?: string;
  };
  disambiguation?: {
    id: string;
    candidates: string[];
    question: string;
    answers: Array<{
      label: string;
      industry_id: string;
    }>;
  } | null;
  unavailable?: { reason?: string } | null;
  links: {
    conversation_id?: string | null;
    artifact_message_id?: string | null;
    operating_system_id?: string | null;
  };
  pending_email?: string | null;
  _preview_snapshot?: PreviewWizardV3Snapshot | null;
}

const metadata = metadataRaw as RouterMetadata;
const BUILD_INTERLUDE_MS = 20000;
const ROUTING_INTERLUDE_MS = 1600;
const ANSWER_FEEDBACK_MIN_MS = 320;
const previewRoutingInterludeMs = 700;
const routingMessages = [
  { maxProgress: 100, title: "Your Context Map is ready", label: "Preparing your access" },
];
const buildMessages = [
  { maxProgress: 35, title: "We're setting up your AI toolkit & Documentation", label: " " },
  { maxProgress: 59, title: "Building execution layers...", label: " " },
  { maxProgress: 100, title: "Building operating logic...", label: "AI in action" },
];
const previewArtifactStreamText = `## Quick Reference

This document defines a focused operating system for turning GTM activity into conviction. The operating system is built for one winnable beachhead segment, one real early customer profile, one aligned offer, a limited set of channels, and a disciplined experimentation loop.

### Start Here

First, lock the GTM Focus Layer around one beachhead segment and one early customer profile. Do not run multi-segment GTM until the current segment shows repeatable pain, willingness to pay, and a stable path to value.

Second, install the Decision and Alignment Layer with explicit owners, weekly review rhythm, and conflict rules.

Third, define the Conviction Layer around one North Star Metric, four to six supporting proof points, and one narrative that links customer pain, value delivered, repeatability, and growth potential.

| Layer | Core Question | What Must Be True | Primary Output | Review Rhythm |
| --- | --- | --- | --- | --- |
| GTM Focus Layer | What exactly are we trying to win? | One segment, one ECP, one aligned offer system | Focused GTM plan | Weekly |
| Decision and Alignment Layer | How do we make decisions without friction? | Clear owners, conflict rules, handoffs, review cadence | Operating cadence and governance | Weekly and monthly |

## Executive Summary

A GTM operating system should not behave like a planning archive. It should function as a decision engine that concentrates resources on the smallest winnable market, learns faster than competitors, and translates customer evidence into organizational conviction.`;

type StreamWizardArtifactInput = {
  message: string;
  thinkingMethodId: string;
  conversationId: string;
  resumeToken: string;
  onArtifactText: (text: string) => void;
};

async function streamWizardArtifact({
  message,
  thinkingMethodId,
  conversationId,
  resumeToken,
  onArtifactText,
}: StreamWizardArtifactInput) {
  const session = (await supabase.auth.getSession()).data.session;
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error("Sign in again to build your App.");

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 10 * 60 * 1000);

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/chat-with-thinking-method`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Accept: "text/event-stream",
      },
      body: JSON.stringify({
        message,
        thinkingMethodId,
        dbConversationId: conversationId,
        kind: "ARTF",
        stream: true,
        forceJson: false,
        wizardResumeToken: resumeToken,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`App draft request failed (HTTP ${response.status}): ${errorText.slice(0, 240)}`);
    }

    const reader = response.body?.getReader();
    if (!reader) return await response.json();

    const decoder = new TextDecoder();
    let accumulatedOutput = "";
    let sseBuffer = "";
    let finalPayload: unknown = null;
    let streamFinished = false;

    const readBoundary = () => {
      const lfIndex = sseBuffer.indexOf("\n\n");
      const crlfIndex = sseBuffer.indexOf("\r\n\r\n");
      if (crlfIndex !== -1 && (lfIndex === -1 || crlfIndex < lfIndex)) return { index: crlfIndex, length: 4 };
      if (lfIndex !== -1) return { index: lfIndex, length: 2 };
      return null;
    };

    try {
      while (!streamFinished) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        while (true) {
          const boundary = readBoundary();
          if (!boundary) break;
          const eventBlock = sseBuffer.slice(0, boundary.index);
          sseBuffer = sseBuffer.slice(boundary.index + boundary.length);

          let eventType = "message";
          let dataStr = "";
          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }

          if (!dataStr || dataStr === "[DONE]") continue;

          let parsed: Record<string, unknown>;
          try {
            parsed = JSON.parse(dataStr) as Record<string, unknown>;
          } catch {
            continue;
          }

          if (eventType === "output") {
            if (typeof parsed.full === "string") {
              accumulatedOutput = parsed.full;
            } else if (typeof parsed.delta === "string") {
              accumulatedOutput += parsed.delta;
            }
            onArtifactText(accumulatedOutput);
          } else if (eventType === "done") {
            finalPayload = parsed;
            streamFinished = true;
          } else if (eventType === "error") {
            throw new Error(typeof parsed.message === "string" ? parsed.message : "App draft generation failed.");
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (finalPayload && typeof finalPayload === "object") {
      const payload = finalPayload as Record<string, unknown>;
      const finalText = payload.text ?? payload.response ?? payload.output_text ?? payload.message;
      if (typeof finalText === "string" && finalText.trim()) onArtifactText(finalText);
      return payload;
    }

    throw new Error("The App draft stream ended before completion.");
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("App draft transport timed out.");
    }
    if (error instanceof Error && error.message.startsWith("App draft request failed (HTTP ")) throw error;
    throw new Error(`App draft transport failed: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function tailArtifactText(text: string, maxCharacters = 9000) {
  const normalized = text.replace(/\r/g, "");
  if (normalized.length <= maxCharacters) return normalized;
  const tail = normalized.slice(-maxCharacters);
  const firstBreak = tail.indexOf("\n");
  return firstBreak >= 0 ? tail.slice(firstBreak + 1) : tail;
}

function maskAccountEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return "";

  const [localPart = "", domainPart = ""] = normalized.split("@");
  const prefix = (localPart || normalized).slice(0, 2);
  const suffix = domainPart.split(".").filter(Boolean).pop() || normalized.slice(-3);
  const hiddenLength = Math.max(4, Math.min(8, normalized.length - prefix.length - suffix.length));

  return `${prefix}${"*".repeat(hiddenLength)}${suffix}`;
}

const previewLensNames: Record<string, string> = {
  NEVER: "Never Split the Difference",
  START_NO: "Start with No",
  StoryLearning: "StoryLearning",
  Kimiz_Dalkir: "Knowledge Management in Theory and Practice",
  HABBITS: "Atomic Habits",
  GettingThingsDone: "Getting Things Done",
};

interface PreviewWizardV3QuestionOption {
  answer_id: string;
  icon: string;
  label: string;
  context_value: string;
  internal_meaning: string;
}

interface PreviewWizardV3TraceQuestion {
  question_id: string;
  prompt: string;
  reason_for_question: string;
  options: PreviewWizardV3QuestionOption[];
  selected_answer_id: string | null;
  selected_context: string | null;
}

interface PreviewWizardV3Snapshot {
  router_version: string;
  industry: IndustryMetadata;
  questionnaire: Questionnaire;
  wizard_version: 3;
  v3: {
    question_trace: PreviewWizardV3TraceQuestion[];
    collected_context: string[];
    decision: {
      primary_lens_topic_id: string;
      context_summary: string;
      blueprint_handoff_context: string;
    } | null;
  };
}

interface PreviewQuestionOutput {
  question: string;
  reason_for_question: string;
  options: Array<{
    icon: string;
    label: string;
    context_value: string;
    internal_meaning: string;
  }>;
}

function normalizePreviewLabel(icon: string, label: string) {
  const trimmedIcon = icon.trim();
  const trimmedLabel = label.trim().replace(/\s+/g, " ");
  if (!trimmedIcon || trimmedLabel.startsWith(trimmedIcon)) return trimmedLabel;
  return `${trimmedIcon} ${trimmedLabel}`;
}

function previewQuestionOutput(questionNumber: number, industry: IndustryMetadata): PreviewQuestionOutput {
  const prompts = [
    `What is the main situation behind your ${industry.menu_label.toLowerCase()} goal?`,
    "What would make the result feel clearly useful?",
    "Where does this usually get stuck right now?",
    "Who needs to act differently for this to work?",
    "What constraint matters most in the next few weeks?",
    "What outcome would prove the system is working?",
  ];

  return {
    question: prompts[(questionNumber - 1) % prompts.length],
    reason_for_question: "Collects concrete context for Lens selection.",
    options: [
      {
        icon: "🎯",
        label: "The priority is clearer focus",
        context_value: `q${questionNumber}: needs sharper focus`,
        internal_meaning: "focus",
      },
      {
        icon: "🧩",
        label: "The pieces are not connected",
        context_value: `q${questionNumber}: needs connected steps`,
        internal_meaning: "structure",
      },
      {
        icon: "⚡",
        label: "Execution is too slow",
        context_value: `q${questionNumber}: needs faster execution`,
        internal_meaning: "execution",
      },
      {
        icon: "🤝",
        label: "People are not aligned",
        context_value: `q${questionNumber}: needs alignment`,
        internal_meaning: "alignment",
      },
      {
        icon: "📌",
        label: "The next action is unclear",
        context_value: `q${questionNumber}: needs next action clarity`,
        internal_meaning: "next_action",
      },
    ],
  };
}

function projectPreviewQuestionnaire(snapshot: PreviewWizardV3Snapshot) {
  return {
    questionnaire_id: snapshot.questionnaire.questionnaire_id,
    title: snapshot.questionnaire.title,
    sections: snapshot.questionnaire.sections,
  };
}

function buildPreviewAnswerTrace(snapshot: PreviewWizardV3Snapshot) {
  return snapshot.v3.question_trace.map((question) => {
    const customAnswer = getCustomRouterAnswerText(question.selected_answer_id ?? undefined);
    const selectedOption = question.selected_answer_id
      ? question.options.find((option) => option.answer_id === question.selected_answer_id) ?? null
      : null;
    return {
      section_id: "dynamic_context",
      section_label: "Context collected",
      question_id: question.question_id,
      prompt: question.prompt,
      answer_id: customAnswer ? CUSTOM_ROUTER_ANSWER_ID : selectedOption?.answer_id ?? null,
      answer_label: customAnswer ?? selectedOption?.label ?? "Not collected",
      score_values: {},
      context_value: customAnswer ?? selectedOption?.context_value ?? null,
    };
  });
}

function previewResult(snapshot: PreviewWizardV3Snapshot) {
  if (!snapshot.v3.decision) {
    const firstMissing = snapshot.v3.question_trace.find((question) => !question.selected_answer_id);
    return {
      complete: false,
      missing_question_ids: firstMissing ? [firstMissing.question_id] : [],
      primary_lens_topic_id: null,
      supporting_lens_topic_ids: [],
      score_trace: null,
    };
  }
  const topic = snapshot.v3.decision.primary_lens_topic_id;
  return {
    complete: true,
    missing_question_ids: [],
    primary_lens_topic_id: topic,
    supporting_lens_topic_ids: [],
    score_trace: null,
    answer_trace: buildPreviewAnswerTrace(snapshot),
    context_summary: snapshot.v3.decision.context_summary,
    primary_lens: {
      id: "preview-lens-uuid",
      topic,
      name: previewLensNames[topic] ?? topic,
    },
    supporting_lenses: [],
  };
}

function createPreviewSnapshot(industryId: string): PreviewWizardV3Snapshot | null {
  const industry = metadata.industries.find((item) => item.id === industryId) ?? null;
  if (!industry || industry.status !== "active" || !industry.questionnaire_id) return null;
  const baseQuestionnaire = metadata.questionnaires[industry.questionnaire_id];
  if (!baseQuestionnaire || baseQuestionnaire.industry_id !== industry.id) {
    throw new Error(`Industry '${industryId}' has an invalid questionnaire`);
  }
  return {
    router_version: metadata.router_version,
    industry: structuredClone(industry),
    questionnaire: {
      questionnaire_id: `${industry.id}_v3_dynamic`,
      industry_id: industry.id,
      title: `Build context for ${industry.menu_label}`,
      candidate_lens_topic_ids: [...baseQuestionnaire.candidate_lens_topic_ids],
      default_lens_topic_id: baseQuestionnaire.default_lens_topic_id,
      tie_break_question_ids: [],
      sections: [{ id: "dynamic_context", label: "Context collected" }],
      questions: [],
    },
    wizard_version: 3,
    v3: {
      question_trace: [],
      collected_context: [],
      decision: null,
    },
  };
}

function appendPreviewQuestion(
  snapshot: PreviewWizardV3Snapshot,
  output: PreviewQuestionOutput,
): PreviewWizardV3Snapshot {
  const questionNumber = snapshot.questionnaire.questions.length + 1;
  const questionId = `v3_${snapshot.industry.id}_q${String(questionNumber).padStart(2, "0")}`;
  const answerLetters = ["a", "b", "c", "d", "e"];
  const options = output.options.map((option, index): PreviewWizardV3QuestionOption => ({
    answer_id: `${questionId}_${answerLetters[index]}`,
    icon: option.icon,
    label: normalizePreviewLabel(option.icon, option.label),
    context_value: option.context_value,
    internal_meaning: option.internal_meaning,
  }));
  const routerQuestion: RouterQuestion = {
    question_id: questionId,
    section_id: "dynamic_context",
    prompt: output.question,
    options: options.map((option) => ({
      answer_id: option.answer_id,
      label: option.label,
      score_values: {},
    })),
  };
  return {
    ...snapshot,
    questionnaire: {
      ...snapshot.questionnaire,
      questions: [...snapshot.questionnaire.questions, routerQuestion],
    },
    v3: {
      ...snapshot.v3,
      decision: null,
      question_trace: [...snapshot.v3.question_trace, {
        question_id: questionId,
        prompt: output.question,
        reason_for_question: output.reason_for_question,
        options,
        selected_answer_id: null,
        selected_context: null,
      }],
    },
  };
}

function recordPreviewAnswer(
  snapshot: PreviewWizardV3Snapshot,
  answers: RouterAnswerMap,
  questionId: string,
  answerId: string,
): { snapshot: PreviewWizardV3Snapshot; answers: RouterAnswerMap } {
  const questionIndex = snapshot.questionnaire.questions.findIndex((question) => question.question_id === questionId);
  if (questionIndex < 0) throw new Error(`Unknown Wizard V3 question '${questionId}'`);
  const traceQuestion = snapshot.v3.question_trace[questionIndex];
  const customAnswer = getCustomRouterAnswerText(answerId);
  const selectedOption = customAnswer
    ? null
    : traceQuestion.options.find((option) => option.answer_id === answerId) ?? null;
  if (!customAnswer && !selectedOption) throw new Error("Unknown Wizard V3 answer");

  const keptQuestions = snapshot.questionnaire.questions.slice(0, questionIndex + 1);
  const keptTrace = snapshot.v3.question_trace.slice(0, questionIndex + 1);
  const nextAnswers: RouterAnswerMap = {};
  for (const keptQuestion of keptQuestions) {
    const selectedAnswerId = keptQuestion.question_id === questionId ? answerId : answers[keptQuestion.question_id];
    if (selectedAnswerId) nextAnswers[keptQuestion.question_id] = selectedAnswerId;
  }

  const selectedTrace = keptTrace.map((item) => {
    const selectedAnswerId = nextAnswers[item.question_id] ?? null;
    const selectedCustomAnswer = getCustomRouterAnswerText(selectedAnswerId ?? undefined);
    const selected = selectedAnswerId
      ? item.options.find((option) => option.answer_id === selectedAnswerId) ?? null
      : null;
    return {
      ...item,
      selected_answer_id: selectedAnswerId,
      selected_context: selectedCustomAnswer ?? selected?.context_value ?? null,
    };
  });

  return {
    answers: nextAnswers,
    snapshot: {
      ...snapshot,
      questionnaire: {
        ...snapshot.questionnaire,
        questions: keptQuestions,
      },
      v3: {
        ...snapshot.v3,
        question_trace: selectedTrace,
        collected_context: selectedTrace
          .map((item) => item.selected_context)
          .filter((item): item is string => !!item),
        decision: null,
      },
    },
  };
}

function recordPreviewAnswerPreservingLater(
  snapshot: PreviewWizardV3Snapshot,
  answers: RouterAnswerMap,
  questionId: string,
  answerId: string,
): { snapshot: PreviewWizardV3Snapshot; answers: RouterAnswerMap } {
  const questionIndex = snapshot.questionnaire.questions.findIndex((question) => question.question_id === questionId);
  if (questionIndex < 0) throw new Error(`Unknown Wizard V3 question '${questionId}'`);
  const traceQuestion = snapshot.v3.question_trace[questionIndex];
  const customAnswer = getCustomRouterAnswerText(answerId);
  const selectedOption = customAnswer
    ? null
    : traceQuestion.options.find((option) => option.answer_id === answerId) ?? null;
  if (!customAnswer && !selectedOption) throw new Error("Unknown Wizard V3 answer");

  const nextAnswers: RouterAnswerMap = {
    ...answers,
    [questionId]: answerId,
  };
  const selectedTrace = snapshot.v3.question_trace.map((item) => {
    const selectedAnswerId = nextAnswers[item.question_id] ?? item.selected_answer_id ?? null;
    const selectedCustomAnswer = getCustomRouterAnswerText(selectedAnswerId ?? undefined);
    const selected = selectedAnswerId
      ? item.options.find((option) => option.answer_id === selectedAnswerId) ?? null
      : null;
    return {
      ...item,
      selected_answer_id: selectedAnswerId,
      selected_context: selectedCustomAnswer ?? selected?.context_value ?? null,
    };
  });

  return {
    answers: nextAnswers,
    snapshot: {
      ...snapshot,
      v3: {
        ...snapshot.v3,
        question_trace: selectedTrace,
        collected_context: selectedTrace
          .map((item) => item.selected_context)
          .filter((item): item is string => !!item),
        decision: null,
      },
    },
  };
}

function setPreviewDecision(snapshot: PreviewWizardV3Snapshot): PreviewWizardV3Snapshot {
  return {
    ...snapshot,
    v3: {
      ...snapshot.v3,
      decision: {
        primary_lens_topic_id: snapshot.questionnaire.default_lens_topic_id,
        context_summary: "The user has enough concrete context to build a focused App.",
        blueprint_handoff_context:
          "Build around the user's stated outcome, current blocker, constraints, and next proof point.",
      },
    },
  };
}

function createPreviewWizardState({
  industryId,
  generatedQuestions,
  answeredQuestions,
  complete,
}: {
  industryId: string;
  generatedQuestions: number;
  answeredQuestions: number;
  complete?: boolean;
}) {
  let snapshot = createPreviewSnapshot(industryId);
  if (!snapshot) return null;
  let answers: RouterAnswerMap = {};
  const questionCount = Math.max(generatedQuestions, answeredQuestions);

  for (let index = 0; index < questionCount; index += 1) {
    snapshot = appendPreviewQuestion(snapshot, previewQuestionOutput(index + 1, snapshot.industry));
    if (index < answeredQuestions) {
      const question = snapshot.questionnaire.questions[index];
      const selectedAnswerId = question.options[index % question.options.length].answer_id;
      const recorded = recordPreviewAnswer(snapshot, answers, question.question_id, selectedAnswerId);
      snapshot = recorded.snapshot;
      answers = recorded.answers;
    }
  }

  if (complete) snapshot = setPreviewDecision(snapshot);

  return { snapshot, answers };
}

function previewRun(state: string): WizardRun {
  const industryId = state === "education" ? "education" : state === "unavailable" ? "other" : "negotiation";
  const industry = metadata.industries.find((item) => item.id === industryId) ?? null;
  const generated = state === "question"
    ? createPreviewWizardState({ industryId, generatedQuestions: 1, answeredQuestions: 0 })
    : state === "industry" || state === "restart" || state === "unavailable" || state === "unsupported"
    ? null
    : createPreviewWizardState({ industryId, generatedQuestions: 10, answeredQuestions: 10, complete: true });
  const snapshot = generated?.snapshot ?? null;
  const answers = generated?.answers ?? {};
  const result = snapshot ? previewResult(snapshot) : null;
  return {
    run_id: "preview-run",
    wizard_version: 3,
    status: state === "restart"
      ? "restart_required"
      : state === "unsupported"
        ? "unsupported"
      : state === "industry"
        ? "industry_selection"
        : state === "unavailable"
          ? "questionnaire_unavailable"
          : state === "verify-email"
            ? "email_verification_pending"
            : state === "gate" || state === "checkout"
            ? "account_gate"
            : "questions",
    resume_expires_at: new Date(Date.now() + 86400000).toISOString(),
    desired_outcome: state === "unsupported"
      ? "Build a custom CRM integration and deploy it to production"
      : "Build a practical operating system for my next business goal",
    industries: metadata.industries,
    industry: state === "industry" || state === "restart" || state === "unsupported" ? null : industry,
    questionnaire: snapshot ? projectPreviewQuestionnaire(snapshot) : null,
    questions: snapshot?.questionnaire.questions ?? [],
    answers,
    result,
    disambiguation: null,
    unavailable: state === "unavailable"
      ? { reason: "This industry questionnaire is coming soon. Choose another industry to continue." }
      : null,
    links: {},
    _preview_snapshot: snapshot,
  };
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

async function edgeFunctionErrorMessage(error: unknown, fallback: string) {
  const context = error && typeof error === "object" && "context" in error
    ? (error as { context?: unknown }).context
    : undefined;

  if (context && typeof (context as { json?: unknown }).json === "function") {
    try {
      const response = context as { clone?: () => { json?: () => Promise<unknown> }; json: () => Promise<unknown> };
      const payload: unknown = await (response.clone?.().json?.() ?? response.json());
      if (payload && typeof payload === "object" && "error" in payload) {
        const message = (payload as { error?: unknown }).error;
        if (typeof message === "string" && message.trim()) return message;
      }
    } catch {
      // Fall back to the Supabase error message below.
    }
  }

  if (error instanceof Error && error.message !== "Edge Function returned a non-2xx status code") {
    return error.message;
  }
  return fallback;
}

export default function Wizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAnonymous, refreshSession } = useAuth();
  const resumeToken = searchParams.get("resume") ?? "";
  const verificationToken = searchParams.get("verify") ?? "";
  const preview = import.meta.env.DEV ? searchParams.get("preview") : null;
  const previewAuthenticated = preview === "authenticated";
  const canAutoBuild = previewAuthenticated || (!!user && !isAnonymous);
  const [run, setRun] = useState<WizardRun | null>(() => preview ? previewRun(preview) : null);
  const [loading, setLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number | null>(null);
  const [editingReviewAnswers, setEditingReviewAnswers] = useState(false);
  const [reviewEditMode, setReviewEditMode] = useState<ReviewEditMode | null>(null);
  const [reviewEditTargetAnswerCount, setReviewEditTargetAnswerCount] = useState<number | null>(null);
  const [pendingReviewEdit, setPendingReviewEdit] = useState<PendingReviewEdit | null>(null);
  const [showCustomAnswer, setShowCustomAnswer] = useState(false);
  const [customAnswer, setCustomAnswer] = useState("");
  const [pendingAnswerId, setPendingAnswerId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [building, setBuilding] = useState(preview === "building");
  const [buildProgress, setBuildProgress] = useState(preview === "building" ? 42 : 0);
  const [buildArtifactText, setBuildArtifactText] = useState("");
  const [routingInterlude, setRoutingInterlude] = useState(preview === "routing");
  const [routingProgress, setRoutingProgress] = useState(preview === "routing" ? 76 : 0);
  const [authenticatedBuildStarted, setAuthenticatedBuildStarted] = useState(false);
  const buildStartInFlightRef = useRef(false);
  const autoVerificationStartedRef = useRef(false);
  const questionHeadingRef = useRef<HTMLHeadingElement>(null);
  const [accessStep, setAccessStep] = useState<AccessStep>(
    preview === "checkout" ? "checkout" : preview === "verify-email" ? "verify-email" : "email",
  );
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("promo");
  const [showPassword, setShowPassword] = useState(false);
  const [account, setAccount] = useState<WizardAccount>({
    name: "",
    email: "",
    confirmEmail: "",
    password: "",
    promoCode: preview === "checkout" ? "APPLYT-3AD4-0DD4-FD1D-DF23" : "",
    cardNumber: "",
    expiry: "",
    cvc: "",
    cardName: "",
  });

  const invokeRouter = async (action: string, body: Record<string, unknown> = {}) => {
    const { data, error: invokeError } = await supabase.functions.invoke("gate-router", {
      body: { action, resumeToken, ...body },
    });
    if (invokeError) {
      const status = getSupabaseFunctionHttpStatus(invokeError);
      throw new Error(`${await edgeFunctionErrorMessage(invokeError, "Wizard request failed.")} ${status ? `(HTTP ${status}) ` : ""}(run ${run?.run_id ?? "unknown"})`);
    }
    if (data?.error) throw new Error(`${data.error} (run ${run?.run_id ?? "unknown"})`);
    return data;
  };

  const awaitBuildRecovery = async (initial: Record<string, unknown>) => {
    let state = initial;
    const deadline = Date.now() + 12 * 60_000;
    while ((state.state === "generating" || state.state === "preparing" || state.state === "queueing") && Date.now() < deadline) {
      const seconds = typeof state.retry_after_seconds === "number" ? state.retry_after_seconds : 5;
      await new Promise((resolve) => window.setTimeout(resolve, Math.max(2, seconds) * 1000));
      state = await invokeRouter("prepare_artifact");
    }
    if (["generating", "preparing", "queueing"].includes(String(state.state))) {
      throw new Error(`App build is still running. Try again in a moment (run ${run?.run_id ?? "unknown"}).`);
    }
    return state;
  };

  const artifactRequestFrom = (state: Record<string, unknown>) => {
    if (typeof state.artifact_request !== "string" ||
        typeof state.thinking_method_id !== "string" ||
        typeof state.conversation_id !== "string") {
      throw new Error(`App draft is not ready (run ${run?.run_id ?? "unknown"}).`);
    }
    return {
      message: state.artifact_request,
      thinkingMethodId: state.thinking_method_id,
      conversationId: state.conversation_id,
    };
  };

  useEffect(() => {
    if (preview) return;
    if (!resumeToken) {
      setError("This wizard link is missing its resume token.");
      setLoading(false);
      return;
    }
    invokeRouter("resume")
      .then((data) => setRun(data.run))
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Unable to resume the wizard."))
      .finally(() => setLoading(false));
    // The resume token is stable for the route lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeToken, preview]);

  useEffect(() => {
    if (!routingInterlude || preview === "routing") return;
    const duration = preview ? previewRoutingInterludeMs : ROUTING_INTERLUDE_MS;
    const startedAt = Date.now();
    setRoutingProgress(12);
    const timer = window.setInterval(() => {
      const ratio = Math.min((Date.now() - startedAt) / duration, 1);
      setRoutingProgress(Math.round(12 + ratio * 88));
      if (ratio >= 1) {
        window.clearInterval(timer);
        setRoutingInterlude(false);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [routingInterlude, preview]);

  useEffect(() => {
    if (preview !== "building" || !building) return;

    let cursor = 0;
    setBuildArtifactText("");
    const timer = window.setInterval(() => {
      cursor = Math.min(previewArtifactStreamText.length, cursor + 22);
      setBuildArtifactText(previewArtifactStreamText.slice(0, cursor));
      if (cursor >= previewArtifactStreamText.length) {
        window.clearInterval(timer);
      }
    }, 55);

    return () => window.clearInterval(timer);
  }, [building, preview]);

  useEffect(() => {
    if (run?.status === "email_verification_pending" && (verificationToken || !canAutoBuild) && accessStep !== "verify-email") {
      setAccessStep("verify-email");
    }
  }, [accessStep, canAutoBuild, run?.status, verificationToken]);

  useEffect(() => {
    if (!run?.pending_email) return;
    setAccount((current) => current.email ? current : { ...current, email: run.pending_email ?? "" });
  }, [run?.pending_email]);

  const firstMissingIndex = useMemo(() => {
    if (!run) return 0;
    const missing = run.questions.findIndex((question) => !run.answers[question.question_id]);
    return missing < 0 ? run.questions.length : missing;
  }, [run]);
  const questionIndex = activeQuestionIndex ?? firstMissingIndex;
  const question = run?.questions[questionIndex] ?? null;
  const questionsComplete = !!run?.result?.complete;
  const reviewRequired = !!run && questionsComplete && run.status === "questions";
  const showReview = reviewRequired && !editingReviewAnswers && !building && !routingInterlude;
  const showAccessFlow = !!run && questionsComplete && run.status !== "questions" && !building && !routingInterlude;
  const currentSection = question
    ? run?.questionnaire?.sections.find((section) => section.id === question.section_id) ?? null
    : null;
  const currentSectionIndex = currentSection
    ? run?.questionnaire?.sections.findIndex((section) => section.id === currentSection.id) ?? 0
    : 0;
  const sectionQuestions = currentSection
    ? run?.questions.filter((item) => item.section_id === currentSection.id) ?? []
    : [];
  const sectionQuestionIndex = question
    ? sectionQuestions.findIndex((item) => item.question_id === question.question_id)
    : 0;
  const customAnswerAllowed = wizardAllowsCustomAnswer(run?.wizard_version, run?.allow_custom_answer);
  const customAnswerApplying = customAnswerAllowed && saving && pendingAnswerId === CUSTOM_ROUTER_ANSWER_ID;
  const customAnswerSelected = customAnswerAllowed && (customAnswerApplying || (!pendingAnswerId && showCustomAnswer));
  const questionProgress = run
    ? getWizardQuestionProgress({
      wizardVersion: run.wizard_version,
      questionIndex,
      totalQuestions: run.questions.length,
      sectionQuestionIndex,
      sectionQuestionsLength: sectionQuestions.length,
    })
    : null;

  useEffect(() => {
    if (!question) return;
    if (!customAnswerAllowed) {
      setCustomAnswer("");
      setShowCustomAnswer(false);
    } else {
      const storedCustomAnswer = getCustomRouterAnswerText(run?.answers[question.question_id]);
      setCustomAnswer(storedCustomAnswer ?? "");
      setShowCustomAnswer(!!storedCustomAnswer);
    }
    window.scrollTo({ top: 0, behavior: "auto" });
    questionHeadingRef.current?.focus({ preventScroll: true });
  }, [customAnswerAllowed, question, run?.answers]);

  const startBuildProgressRamp = () => {
    const startedAt = Date.now();
    setBuildProgress(18);
    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const ratio = Math.min(elapsed / BUILD_INTERLUDE_MS, 1);
      const nextProgress = Math.round(18 + ratio * 64);
      setBuildProgress((current) => Math.max(current, nextProgress));
      if (ratio >= 1) {
        window.clearInterval(timer);
      }
    }, 250);
    return () => window.clearInterval(timer);
  };

  const resetReviewEditState = () => {
    setEditingReviewAnswers(false);
    setReviewEditMode(null);
    setReviewEditTargetAnswerCount(null);
    setPendingReviewEdit(null);
  };

  const beginReviewEdit = (edit: PendingReviewEdit, mode: ReviewEditMode) => {
    setError("");
    setPendingReviewEdit(null);
    setReviewEditMode(mode);
    setReviewEditTargetAnswerCount(edit.answerCount);
    setEditingReviewAnswers(true);
    setActiveQuestionIndex(edit.questionIndex);
  };

  const answerQuestion = async (answerId: string, customAnswerValue = "") => {
    if (!run || !question || saving) return;
    if (!customAnswerAllowed && answerId === CUSTOM_ROUTER_ANSWER_ID) return;
    const feedbackStartedAt = Date.now();
    if (answerId !== CUSTOM_ROUTER_ANSWER_ID) setShowCustomAnswer(false);
    setPendingAnswerId(answerId);
    setSaving(true);
    setError("");
    try {
      if (preview) {
        const snapshot = run._preview_snapshot;
        if (!snapshot) return;
        const storedAnswer = answerId === CUSTOM_ROUTER_ANSWER_ID
          ? createCustomRouterAnswer(customAnswerValue)
          : answerId;
        const recorded = reviewEditMode === "single"
          ? recordPreviewAnswerPreservingLater(snapshot, run.answers, question.question_id, storedAnswer)
          : recordPreviewAnswer(snapshot, run.answers, question.question_id, storedAnswer);
        const answeredCount = recorded.snapshot.v3.question_trace.filter((item) => !!item.selected_answer_id).length;
        const targetAnswerCount = reviewEditMode === "dependent"
          ? Math.min(reviewEditTargetAnswerCount ?? answeredCount, 10)
          : null;
        const nextSnapshot = reviewEditMode === "single" || (targetAnswerCount ? answeredCount >= targetAnswerCount : answeredCount >= 6)
          ? setPreviewDecision(recorded.snapshot)
          : appendPreviewQuestion(
            recorded.snapshot,
            previewQuestionOutput(recorded.snapshot.questionnaire.questions.length + 1, recorded.snapshot.industry),
          );
        const nextResult = previewResult(nextSnapshot);
        const remainingFeedbackMs = ANSWER_FEEDBACK_MIN_MS - (Date.now() - feedbackStartedAt);
        if (remainingFeedbackMs > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, remainingFeedbackMs));
        }
        setRun({
          ...run,
          _preview_snapshot: nextSnapshot,
          questionnaire: projectPreviewQuestionnaire(nextSnapshot),
          questions: nextSnapshot.questionnaire.questions,
          answers: recorded.answers,
          result: nextResult,
        });
        setActiveQuestionIndex(questionIndex + 1);
        if (nextResult.complete && (!questionsComplete || editingReviewAnswers)) {
          if (editingReviewAnswers) resetReviewEditState();
          setRoutingInterlude(true);
        }
        return;
      }
      const reviewEditPayload = editingReviewAnswers
        ? {
          preserveLaterAnswers: reviewEditMode === "single" || undefined,
          reviewEditTargetAnswerCount: reviewEditMode === "dependent"
            ? reviewEditTargetAnswerCount ?? run.questions.length
            : undefined,
        }
        : {};
      const data = await invokeRouter("answer", {
        questionId: question.question_id,
        answerId,
        customAnswer: answerId === CUSTOM_ROUTER_ANSWER_ID ? customAnswerValue : undefined,
        ...reviewEditPayload,
      });
      const remainingFeedbackMs = ANSWER_FEEDBACK_MIN_MS - (Date.now() - feedbackStartedAt);
      if (remainingFeedbackMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remainingFeedbackMs));
      }
      setRun(data.run);
      setActiveQuestionIndex(questionIndex + 1);
      if (data.run?.result?.complete && (!questionsComplete || editingReviewAnswers)) {
        if (editingReviewAnswers) resetReviewEditState();
        setRoutingInterlude(true);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to save the answer.");
    } finally {
      setPendingAnswerId(null);
      setSaving(false);
    }
  };

  const selectIndustry = async (industryId: string) => {
    if (!run || saving) return;
    setSaving(true);
    setError("");
    setActiveQuestionIndex(null);
    resetReviewEditState();
    try {
      if (preview) {
        const industry = metadata.industries.find((item) => item.id === industryId) ?? null;
        if (!industry) throw new Error("Unknown industry.");
        const generated = createPreviewWizardState({ industryId, generatedQuestions: 1, answeredQuestions: 0 });
        const snapshot = generated?.snapshot ?? null;
        setRun({
          ...run,
          status: snapshot ? "questions" : "questionnaire_unavailable",
          industry,
          questionnaire: snapshot ? projectPreviewQuestionnaire(snapshot) : null,
          questions: snapshot?.questionnaire.questions ?? [],
          answers: generated?.answers ?? {},
          result: null,
          disambiguation: null,
          unavailable: snapshot
            ? null
            : { reason: `The ${industry.menu_label} questionnaire is coming soon. Choose another industry to continue.` },
          _preview_snapshot: snapshot,
        });
        return;
      }
      const data = await invokeRouter("select_industry", { industryId });
      setRun(data.run);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to select the industry.");
    } finally {
      setSaving(false);
    }
  };

  const changeIndustry = () => {
    if (!run) return;
    setError("");
    setActiveQuestionIndex(null);
    resetReviewEditState();
    setRun({
      ...run,
      status: "industry_selection",
      industry: null,
      questionnaire: null,
      questions: [],
      answers: {},
      result: null,
      disambiguation: null,
      unavailable: null,
      _preview_snapshot: null,
    });
  };

  const editReviewAnswer = (questionId: string) => {
    if (!run) return;
    const index = run.questions.findIndex((item) => item.question_id === questionId);
    if (index < 0) return;
    const answeredQuestionCount = run.questions.filter((item) => !!run.answers[item.question_id]).length;
    const answerCount = Math.max(run.result?.answer_trace?.length ?? 0, answeredQuestionCount, run.questions.length);
    const affectedLabel = getWizardReviewEditAffectedLabel({ questionIndex: index, answerCount });
    const edit = {
      questionId,
      questionIndex: index,
      answerCount,
      affectedLabel: affectedLabel ?? "",
    };
    setError("");
    if (affectedLabel) {
      setPendingReviewEdit(edit);
      return;
    }
    beginReviewEdit(edit, "dependent");
  };

  const continueFromReview = async () => {
    if (!run || saving) return;
    setSaving(true);
    setError("");
    try {
      await confirmOutcomeForAccountGate();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to confirm your blueprint inputs.");
    } finally {
      setSaving(false);
    }
  };

  const generateArtifactAndOs = async () => {
    if (buildStartInFlightRef.current) return;
    buildStartInFlightRef.current = true;
    if (preview) {
      setBuilding(true);
      setBuildProgress(42);
      return;
    }
    setBuilding(true);
    setBuildProgress(18);
    setBuildArtifactText("");
    setError("");
    let stopProgressRamp: (() => void) | null = null;
    try {
      stopProgressRamp = startBuildProgressRamp();
      let prepared = await awaitBuildRecovery(await invokeRouter("prepare_artifact"));
      if (prepared.operating_system_id) {
        navigate(getWizardOsNavigation(prepared));
        return;
      }
      setBuildProgress((current) => Math.max(current, 36));
      let chatData: Record<string, unknown> | null = null;
      try {
        chatData = await streamWizardArtifact({
          ...artifactRequestFrom(prepared),
          resumeToken,
          onArtifactText: setBuildArtifactText,
        });
      } catch (streamError) {
        // The server may have finished and persisted the artifact after iOS lost SSE.
        prepared = await awaitBuildRecovery(await invokeRouter("prepare_artifact"));
        if (prepared.operating_system_id) {
          navigate(getWizardOsNavigation(prepared));
          return;
        }
        if (prepared.state !== "ready") throw streamError;
        chatData = await streamWizardArtifact({
          ...artifactRequestFrom(prepared),
          resumeToken,
          onArtifactText: setBuildArtifactText,
        });
      }
      stopProgressRamp();
      stopProgressRamp = null;
      setBuildProgress((current) => Math.max(current, 86));
      const artifactMessageId = getAssistantArtifactId(chatData);
      if (!artifactMessageId) throw new Error("App draft finished without a source message.");
      setBuildProgress(94);
      const queued = await awaitBuildRecovery(await invokeRouter("queue_os", { artifactMessageId }));
      setBuildProgress(100);
      navigate(getWizardOsNavigation(queued));
    } catch (reason) {
      stopProgressRamp?.();
      buildStartInFlightRef.current = false;
      setError(reason instanceof Error ? reason.message : "Unable to build the App.");
      setBuilding(false);
    }
  };

  const confirmOutcomeForAccountGate = async () => {
    if (!run) return;
    if (run.status === "outcome_confirmed") return;
    if (preview) {
      setRun({ ...run, status: canAutoBuild ? "outcome_confirmed" : "account_gate" });
      return;
    }
    const data = await invokeRouter("confirm_outcome");
    setRun(data.run);
  };

  const startAuthenticatedBuild = async () => {
    if (!run || saving) return;
    setSaving(true);
    setError("");
    try {
      if (["questions", "account_gate", "email_verification_pending"].includes(run.status)) {
        await confirmOutcomeForAccountGate();
      }
      await generateArtifactAndOs();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to start the App build.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!showAccessFlow || !canAutoBuild || authenticatedBuildStarted || building) return;
    setAuthenticatedBuildStarted(true);
    void startAuthenticatedBuild();
    // startAuthenticatedBuild is intentionally not a dependency; this effect is a one-shot handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAccessFlow, canAutoBuild, authenticatedBuildStarted, building]);

  const updateAccountField = <K extends keyof WizardAccount>(field: K, value: WizardAccount[K]) => {
    setAccount((current) => ({ ...current, [field]: value }));
    if (error) setError("");
  };

  const openLoginStep = (message = "", resetPassword = false) => {
    setAccount((current) => ({
      ...current,
      email: current.email.trim().toLowerCase(),
      password: resetPassword ? "" : current.password,
    }));
    setError(message);
    setAccessStep("login");
  };

  const continueToConfirmEmail = () => {
    const email = account.email.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setAccount((value) => ({ ...value, email, confirmEmail: "" }));
    setError("");
    setAccessStep("confirm-email");
  };

  const continueToPassword = () => {
    if (account.confirmEmail.trim().toLowerCase() !== account.email) {
      setError("Email addresses do not match.");
      return;
    }
    setError("");
    setAccessStep("password");
  };

  const continueToName = () => {
    if (account.password.length < 8) {
      setError("Create a password with at least 8 characters.");
      return;
    }
    setError("");
    setAccessStep("name");
  };

  const continueToCheckout = async () => {
    if (!account.name.trim()) {
      setError("Enter your name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await confirmOutcomeForAccountGate();
      setAccessStep("checkout");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to save your route.");
    } finally {
      setSaving(false);
    }
  };

  const signInAndResume = async () => {
    setSaving(true);
    setError("");
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: account.email.trim().toLowerCase(),
        password: account.password,
      });
      if (signInError) throw signInError;
      await confirmOutcomeForAccountGate();
      await generateArtifactAndOs();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to sign in.");
    } finally {
      setSaving(false);
    }
  };

  const signUpAndResume = async () => {
    if (!account.promoCode.trim()) {
      setError("Enter a promo code to continue in DEV.");
      return;
    }
    if (preview) {
      setRun((current) => current ? { ...current, status: "email_verification_pending" } : current);
      setAccessStep("verify-email");
      return;
    }
    setSaving(true);
    setError("");
    try {
      // Account creation starts only after checkout access is submitted. Build starts after email verification.
      const { data, error: signupError } = await supabase.functions.invoke("signup-with-wizard-promo", {
        body: {
          name: account.name,
          email: account.email,
          password: account.password,
          promoCode: account.promoCode,
          resumeToken,
          appOrigin: window.location.origin,
        },
      });
      if (signupError) {
        const message = await edgeFunctionErrorMessage(signupError, "Unable to send verification email.");
        if (isWizardExistingAccountError(message)) {
          openLoginStep(WIZARD_EXISTING_ACCOUNT_LOGIN_MESSAGE, true);
          return;
        }
        throw new Error(message);
      }
      const responseError = typeof data?.error === "string" ? data.error : "";
      if (isWizardExistingAccountError(responseError)) {
        openLoginStep(WIZARD_EXISTING_ACCOUNT_LOGIN_MESSAGE, true);
        return;
      }
      if (!data?.verification_required) throw new Error(responseError || "Unable to send verification email.");
      setRun((current) => current ? { ...current, status: "email_verification_pending" } : current);
      setAccessStep("verify-email");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to send verification email.");
    } finally {
      setSaving(false);
    }
  };

  const continueAfterVerification = async () => {
    setSaving(true);
    setError("");
    let startedBuild = false;
    try {
      if (verificationToken) {
        const { data, error: verifyError } = await supabase.functions.invoke("verify-wizard-email", {
          body: { resumeToken, tokenHash: verificationToken },
        });
        if (verifyError) throw new Error(await edgeFunctionErrorMessage(verifyError, "Unable to verify email."));
        if (data?.error) throw new Error(data.error);
        if (!data?.session) throw new Error("Verification completed without a session.");
        const { error: sessionError } = await supabase.auth.setSession(data.session);
        if (sessionError) throw sessionError;
        await refreshSession();
        navigate(`/wizard?resume=${encodeURIComponent(resumeToken)}`, { replace: true });
      } else if (!user || isAnonymous) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          throw new Error("Open the verification link from your email, then Applyt will continue the build.");
        }
      }
      setAuthenticatedBuildStarted(false);
      startedBuild = true;
      await startAuthenticatedBuild();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to verify email.");
    } finally {
      if (!startedBuild) setSaving(false);
    }
  };

  useEffect(() => {
    if (
      preview ||
      loading ||
      !verificationToken ||
      !showAccessFlow ||
      accessStep !== "verify-email" ||
      building ||
      saving ||
      autoVerificationStartedRef.current
    ) {
      return;
    }
    autoVerificationStartedRef.current = true;
    void continueAfterVerification();
    // continueAfterVerification intentionally stays out of the dependency list:
    // this is a one-shot handoff for the email link's current token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessStep, building, loading, preview, saving, showAccessFlow, verificationToken]);

  if (loading) {
    return <WizardFrame><StateMessage icon={<Loader2 className="h-6 w-6 animate-spin" />} title="Opening your routing session" /></WizardFrame>;
  }
  if (!run) {
    return <WizardFrame><StateMessage title="Wizard unavailable" description={error || "Start again from the Home page."} /></WizardFrame>;
  }
  if (run.status === "restart_required") {
    return (
      <WizardFrame>
        <StateMessage
          title="This Wizard route needs a fresh start"
          description="The routing flow has been upgraded. Start a new route to choose an industry and use the current questionnaire."
        >
          <Button onClick={() => navigate("/")}>Start a new route</Button>
        </StateMessage>
      </WizardFrame>
    );
  }
  if (run.status === "unsupported") {
    return (
      <UnsupportedOutcomeScreen
        desiredOutcome={run.desired_outcome}
        onTryAnother={() => navigate("/")}
        onBrowseExpertise={() => navigate("/lenses")}
      />
    );
  }
  if (run.status === "industry_selection") {
    return (
      <IndustrySelectionScreen
        desiredOutcome={run.desired_outcome}
        industries={run.industries}
        disambiguation={run.disambiguation}
        error={error}
        saving={saving}
        onSelect={selectIndustry}
      />
    );
  }
  if (run.status === "questionnaire_unavailable") {
    return (
      <WizardFrame>
        <StateMessage
          title={`${run.industry?.menu_label ?? "This industry"} is coming soon`}
          description={run.unavailable?.reason || "Choose another industry to continue with an available questionnaire."}
        >
          <Button onClick={changeIndustry}>Choose another industry</Button>
        </StateMessage>
      </WizardFrame>
    );
  }
  if (building) {
    return <BuildProgressScreen progress={buildProgress} artifactText={buildArtifactText} />;
  }
  if (routingInterlude) {
    return <RoutingProgressScreen progress={routingProgress} lens={run.result?.primary_lens ?? null} desiredOutcome={run.desired_outcome} />;
  }
  if (showReview) {
    return (
      <>
        <BlueprintReviewScreen
          run={run}
          saving={saving}
          error={error}
          authenticated={canAutoBuild}
          onContinue={continueFromReview}
          onEditAnswer={editReviewAnswer}
          onChangeIndustry={changeIndustry}
        />
        <ReviewEditConfirmDialog
          edit={pendingReviewEdit}
          onBack={() => setPendingReviewEdit(null)}
          onContinue={() => pendingReviewEdit && beginReviewEdit(pendingReviewEdit, "dependent")}
          onAnswerOnlyThis={() => pendingReviewEdit && beginReviewEdit(pendingReviewEdit, "single")}
        />
      </>
    );
  }
  if (showAccessFlow) {
    if (canAutoBuild) {
      if (authenticatedBuildStarted && error && !building) {
        return (
          <WizardFrame>
            <StateMessage title="Unable to build your App" description={error}>
              <Button onClick={() => { setError(""); setAuthenticatedBuildStarted(false); }}>
                Try again
              </Button>
            </StateMessage>
          </WizardFrame>
        );
      }
      return (
        <BuildProgressScreen progress={Math.max(buildProgress, 18)} artifactText={buildArtifactText} />
      );
    }

    return (
      <AccessFrame>
        {accessStep === "email" && (
          <AccessCard step={1}>
            <AccessEyebrow icon={<Mail className="h-4 w-4" />}>Your Applyt workspace</AccessEyebrow>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] text-[#122140] sm:text-5xl">
              Save your route.<br /><span className="text-[#2d7d8e]">Build your App.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-[#6f84a3] sm:text-xl">
              Enter your email to keep your answers and unlock the App built around your goal.
            </p>
            <form className="mt-9 space-y-7" onSubmit={(event) => { event.preventDefault(); continueToConfirmEmail(); }}>
              <AccessInput
                id="wizard-email"
                label="Email address"
                icon={<Mail className="h-7 w-7" />}
                type="email"
                value={account.email}
                onChange={(value) => updateAccountField("email", value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
              />
              <TrustCallout />
              <AccessError message={error} />
              <WizardPrimaryButton type="submit">Continue <ArrowRight className="h-6 w-6" /></WizardPrimaryButton>
            </form>
            <LegalAndLogin onLogin={() => openLoginStep()} />
          </AccessCard>
        )}

        {accessStep === "confirm-email" && (
          <AccessCard step={2}>
            <AccessEyebrow icon={<Mail className="h-4 w-4" />}>Confirm your email</AccessEyebrow>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] text-[#122140] sm:text-5xl">One quick check</h1>
            <p className="mt-5 text-lg leading-8 text-[#6f84a3] sm:text-xl">
              Confirm your email so we send your Applyt access details to the right address.
            </p>
            <AccountEmail accountEmail={account.email} onChange={() => { setError(""); setAccessStep("email"); }} />
            <form className="mt-7 space-y-7" onSubmit={(event) => { event.preventDefault(); continueToPassword(); }}>
              <AccessInput
                id="wizard-confirm-email"
                label="Confirm email address"
                icon={<Mail className="h-7 w-7" />}
                type="email"
                value={account.confirmEmail}
                onChange={(value) => updateAccountField("confirmEmail", value)}
                placeholder="Type your email again"
                autoComplete="email"
                autoFocus
              />
              <AccessError message={error} />
              <WizardPrimaryButton type="submit">Continue <ArrowRight className="h-6 w-6" /></WizardPrimaryButton>
            </form>
            <ExistingAccountLoginAction onLogin={() => openLoginStep()} />
          </AccessCard>
        )}

        {accessStep === "password" && (
          <AccessCard step={3}>
            <AccessEyebrow icon={<ShieldCheck className="h-4 w-4" />}>Secure your workspace</AccessEyebrow>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] text-[#122140] sm:text-5xl">Create your password</h1>
            <p className="mt-5 text-lg leading-8 text-[#6f84a3] sm:text-xl">Use at least 8 characters to protect your Applyt workspace.</p>
            <AccountEmail accountEmail={account.email} onChange={() => { setError(""); setAccessStep("email"); }} />
            <form className="mt-7 space-y-7" onSubmit={(event) => { event.preventDefault(); continueToName(); }}>
              <AccessInput
                id="wizard-password"
                label="Password"
                icon={<Lock className="h-7 w-7" />}
                type={showPassword ? "text" : "password"}
                value={account.password}
                onChange={(value) => updateAccountField("password", value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                autoFocus
                trailing={(
                  <button
                    type="button"
                    className="rounded-lg p-1 text-[#7892b4] transition hover:text-[#25456f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7d8e]"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                )}
              />
              <p className="flex items-center gap-2 text-sm font-medium text-[#7892b4]">
                <Check className="h-4 w-4 text-[#2d7d8e]" /> Minimum 8 characters
              </p>
              <AccessError message={error} />
              <WizardPrimaryButton type="submit">Continue <ArrowRight className="h-6 w-6" /></WizardPrimaryButton>
            </form>
            <ExistingAccountLoginAction onLogin={() => openLoginStep()} />
          </AccessCard>
        )}

        {accessStep === "name" && (
          <AccessCard step={4}>
            <AccessEyebrow icon={<UserRound className="h-4 w-4" />}>Personalize your workspace</AccessEyebrow>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] text-[#122140] sm:text-5xl">
              What should we<br /><span className="text-[#2d7d8e]">call you?</span>
            </h1>
            <p className="mt-5 text-lg leading-8 text-[#6f84a3] sm:text-xl">Add your name so your new App is ready for your workspace.</p>
            <form className="mt-9 space-y-7" onSubmit={(event) => { event.preventDefault(); void continueToCheckout(); }}>
              <AccessInput
                id="wizard-name"
                label="Full name"
                icon={<UserRound className="h-7 w-7" />}
                value={account.name}
                onChange={(value) => updateAccountField("name", value)}
                placeholder="Your full name"
                autoComplete="name"
                autoFocus
              />
              <AccessError message={error} />
              <WizardPrimaryButton type="submit" disabled={saving}>
                {saving && <Loader2 className="h-5 w-5 animate-spin" />}
                Continue
                <ArrowRight className="h-6 w-6" />
              </WizardPrimaryButton>
            </form>
            <ExistingAccountLoginAction onLogin={() => openLoginStep()} />
          </AccessCard>
        )}

        {accessStep === "checkout" && (
          <CheckoutCard
            account={account}
            error={error}
            mode={paymentMode}
            saving={saving}
            onModeChange={setPaymentMode}
            onAccountChange={setAccount}
            onSubmit={signUpAndResume}
            onLogin={() => openLoginStep()}
          />
        )}

        {accessStep === "verify-email" && (
          <VerificationPendingCard
            email={account.email || run.pending_email || ""}
            error={error}
            saving={saving}
            verificationReady={!!verificationToken}
            onContinue={continueAfterVerification}
          />
        )}

        {accessStep === "login" && (
          <AccessCard>
            <AccessEyebrow icon={<KeyRound className="h-4 w-4" />}>Existing Applyt account</AccessEyebrow>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] text-[#122140] sm:text-5xl">Log in to continue</h1>
            <p className="mt-5 text-lg leading-8 text-[#6f84a3] sm:text-xl">Use your Applyt account to unlock this App route.</p>
            <form className="mt-9 space-y-6" onSubmit={(event) => { event.preventDefault(); void signInAndResume(); }}>
              <AccessInput
                id="wizard-login-email"
                label="Email address"
                icon={<Mail className="h-7 w-7" />}
                type="email"
                value={account.email}
                onChange={(value) => updateAccountField("email", value)}
                placeholder="you@company.com"
                autoComplete="email"
              />
              <AccessInput
                id="wizard-login-password"
                label="Password"
                icon={<KeyRound className="h-7 w-7" />}
                type="password"
                value={account.password}
                onChange={(value) => updateAccountField("password", value)}
                placeholder="Password"
                autoComplete="current-password"
              />
              <AccessError message={error} />
              <WizardPrimaryButton type="submit" disabled={saving}>
                {saving && <Loader2 className="h-5 w-5 animate-spin" />}
                Log in and build
                <ArrowRight className="h-6 w-6" />
              </WizardPrimaryButton>
              <Button type="button" variant="ghost" className="w-full text-[#7892b4]" onClick={() => { setError(""); setAccessStep("email"); }}>
                Back to email access
              </Button>
            </form>
          </AccessCard>
        )}
      </AccessFrame>
    );
  }

  return (
    <WizardFrame>
      {question && (
        <div className="mx-auto max-w-3xl">
          <div className="mb-5">
            <div className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <span>
                Section {currentSectionIndex + 1} of {run.questionnaire?.sections.length ?? 1}
                {currentSection ? ` · ${currentSection.label}` : ""}
              </span>
              <span>{questionProgress?.primaryLabel} · {questionProgress?.secondaryLabel}</span>
            </div>
            <Progress
              className="mt-3 h-1.5"
              value={questionProgress?.value ?? 0}
            />
          </div>
          <Card
            key={question.question_id}
            className="border-primary/15 shadow-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-right-2 motion-safe:duration-300"
          >
            <CardContent className="p-6 sm:p-8">
              <p className="text-sm font-medium text-primary">
                {run.industry?.menu_label} · {run.desired_outcome}
              </p>
              {editingReviewAnswers && (
                <div className="mt-4 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-sm leading-6 text-muted-foreground">
                  {reviewEditMode === "single"
                    ? "You are updating this answer only. Later answers will stay unchanged."
                    : "You are re-checking affected answers. We'll return you to review when this is complete."}
                </div>
              )}
              <h1
                ref={questionHeadingRef}
                tabIndex={-1}
                className="mt-4 text-2xl font-semibold leading-tight outline-none sm:text-3xl"
              >
                {question.prompt}
              </h1>
              <div className="mt-6 grid gap-3">
                {question.options.map((option) => {
                  const applying = saving && pendingAnswerId === option.answer_id;
                  const selected = applying ||
                    (!pendingAnswerId && !showCustomAnswer && run.answers[question.question_id] === option.answer_id);
                  return (
                  <button
                    key={option.answer_id}
                    type="button"
                    onClick={() => answerQuestion(option.answer_id)}
                    disabled={saving}
                    aria-pressed={selected}
                    aria-busy={applying}
                    className={cn(
                      "flex min-h-16 w-full items-center gap-4 rounded-xl border px-4 py-4 text-left transition duration-200 hover:border-primary/45 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait",
                      selected && "border-primary bg-primary/5 shadow-sm",
                      saving && !selected && "opacity-45",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-bold text-muted-foreground",
                        selected && "border-primary bg-primary text-primary-foreground",
                      )}
                      aria-hidden
                    >
                      {selected && <Check className="h-4 w-4" />}
                    </span>
                    <span className="flex-1 font-semibold text-foreground">{option.label}</span>
                    {applying && (
                      <span className="flex shrink-0 items-center gap-2 text-xs font-semibold text-primary" role="status">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Applying...
                      </span>
                    )}
                  </button>
                  );
                })}
                {customAnswerAllowed && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomAnswer(true);
                      setError("");
                    }}
                    disabled={saving}
                    aria-pressed={customAnswerSelected}
                    aria-busy={customAnswerApplying}
                    aria-expanded={showCustomAnswer}
                    aria-controls={`custom-answer-${question.question_id}`}
                    className={cn(
                      "flex min-h-16 w-full items-center gap-4 rounded-xl border px-4 py-4 text-left transition duration-200 hover:border-primary/45 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait",
                      customAnswerSelected && "border-primary bg-primary/5",
                      saving && pendingAnswerId !== CUSTOM_ROUTER_ANSWER_ID && "opacity-45",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-bold text-muted-foreground",
                        customAnswerSelected && "border-primary bg-primary text-primary-foreground",
                      )}
                      aria-hidden
                    >
                      {customAnswerSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className="flex-1 font-semibold text-foreground">✍️ Other</span>
                    {customAnswerApplying && (
                      <span className="flex shrink-0 items-center gap-2 text-xs font-semibold text-primary" role="status">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Applying...
                      </span>
                    )}
                  </button>
                )}
                {customAnswerAllowed && showCustomAnswer && (
                  <div
                    id={`custom-answer-${question.question_id}`}
                    className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4"
                  >
                    <label
                      htmlFor={`custom-answer-input-${question.question_id}`}
                      className="text-sm font-semibold text-foreground"
                    >
                      Write the answer that fits you
                    </label>
                    <Textarea
                      id={`custom-answer-input-${question.question_id}`}
                      value={customAnswer}
                      onChange={(event) => setCustomAnswer(event.target.value)}
                      maxLength={MAX_CUSTOM_ROUTER_ANSWER_LENGTH}
                      rows={3}
                      autoFocus
                      disabled={saving}
                      placeholder="Describe your answer in your own words"
                      className="mt-2 resize-none bg-background"
                    />
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        {customAnswer.length}/{MAX_CUSTOM_ROUTER_ANSWER_LENGTH}
                      </span>
                      <Button
                        type="button"
                        disabled={!customAnswer.trim() || saving}
                        onClick={() => answerQuestion(CUSTOM_ROUTER_ANSWER_ID, customAnswer)}
                      >
                        {customAnswerApplying ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Applying...
                          </>
                        ) : (
                          <>
                            Continue <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <Button variant="ghost" disabled={questionIndex === 0 || saving} onClick={() => setActiveQuestionIndex(Math.max(0, questionIndex - 1))}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <div className="flex items-center gap-3">
                  <Button variant="outline" disabled={saving} onClick={changeIndustry}>
                    Change industry
                  </Button>
                </div>
              </div>
              {error && <p className="mt-3 text-sm text-destructive" role="alert">{error}</p>}
            </CardContent>
          </Card>
        </div>
      )}
    </WizardFrame>
  );
}

function WizardFrame({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/50"><Header hideUnauthenticatedActions /><main className="px-4 py-10 sm:px-6">{children}</main></div>;
}

const industryVisuals: Record<string, { emoji: string; background: string }> = {
  fundraising: { emoji: "💰", background: "#d8f0ff" },
  entrepreneurship: { emoji: "🚀", background: "#dff7ed" },
  go_to_market: { emoji: "📣", background: "#ffe8d7" },
  sales: { emoji: "🤝", background: "#fff0bf" },
  negotiation: { emoji: "♟️", background: "#d9f2ef" },
  business_operations: { emoji: "⚙️", background: "#e7e3ff" },
  leadership: { emoji: "🧭", background: "#dcecff" },
  communication: { emoji: "💬", background: "#ffe0e8" },
  productivity: { emoji: "⚡", background: "#fff0bf" },
  health: { emoji: "🌱", background: "#dff4dd" },
  investing: { emoji: "📈", background: "#d9f0ff" },
  education: { emoji: "🎓", background: "#eadfff" },
  other: { emoji: "❓", background: "#f3dff2" },
};

function IndustrySelectionScreen({
  desiredOutcome,
  industries,
  disambiguation,
  error,
  saving,
  onSelect,
}: {
  desiredOutcome: string;
  industries: IndustryMetadata[];
  disambiguation?: WizardRun["disambiguation"];
  error: string;
  saving: boolean;
  onSelect: (industryId: string) => void;
}) {
  const disambiguationAnswers = disambiguation?.answers
    .map((answer) => ({
      ...answer,
      industry: industries.find((industry) => industry.id === answer.industry_id) ?? null,
    }))
    .filter((answer) => answer.industry?.status === "active") ?? [];
  const hasDisambiguation = !!disambiguation?.question && disambiguationAnswers.length >= 2;

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#061511] px-4 py-6 sm:px-6 sm:py-10">
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          backgroundImage: [
            "radial-gradient(circle at 18% 8%, rgba(45,125,142,0.22), transparent 24rem)",
            "radial-gradient(circle at 86% 88%, rgba(31,78,121,0.2), transparent 28rem)",
            "repeating-linear-gradient(to bottom, transparent 0, transparent 48px, rgba(45,125,142,0.13) 48px, rgba(45,125,142,0.13) 76px)",
          ].join(","),
        }}
      />

      <section
        className="relative mx-auto w-full max-w-2xl rounded-[2rem] bg-white px-5 py-7 shadow-[0_28px_90px_-32px_rgba(0,0,0,0.9)] sm:rounded-[2.5rem] sm:px-10 sm:py-10"
        aria-labelledby="industry-selection-title"
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2d7d8e]">Choose your starting point</p>
          <h1
            id="industry-selection-title"
            className="mt-3 max-w-xl text-3xl font-extrabold leading-[1.08] tracking-[-0.035em] text-[#111827] sm:text-4xl"
          >
            {hasDisambiguation ? "One quick clarification" : "Which industry best fits your outcome?"}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-6 text-[#63748b] sm:text-base">
            {hasDisambiguation
              ? "Choose the answer that best matches your main goal. We will open the right questionnaire from there."
              : "Pick the closest match. We will open its questionnaire and select one expert method from your answers."}
          </p>
          <div className="mt-5 rounded-2xl bg-[#f2f7f8] px-4 py-3">
            <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-[#6b8192]">Your outcome</p>
            <p className="mt-1 text-sm font-semibold leading-5 text-[#173044]">{desiredOutcome}</p>
          </div>
        </div>

        {hasDisambiguation ? (
          <div className="mt-6" aria-label="Industry clarification">
            <p className="text-base font-bold leading-6 text-[#173044]">{disambiguation.question}</p>
            <ul className="mt-4 space-y-2">
              {disambiguationAnswers.map(({ industry, industry_id, label }) => {
                const visual = industryVisuals[industry_id] ?? industryVisuals.other;
                return (
                  <li key={industry_id}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onSelect(industry_id)}
                      className="group flex min-h-[4.75rem] w-full items-center gap-3 rounded-2xl border border-[#d8e5eb] bg-white px-3 py-3 text-left transition hover:border-[#2d7d8e] hover:bg-[#f3f7f8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7d8e] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 sm:gap-4 sm:px-4"
                    >
                      <span
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl shadow-[inset_0_0_0_1px_rgba(255,255,255,0.65)]"
                        style={{ backgroundColor: visual.background }}
                        aria-hidden="true"
                      >
                        {visual.emoji}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-base font-bold leading-5 text-[#111827]">{label}</span>
                        <span className="mt-1 block text-xs leading-4 text-[#6b7d91] sm:text-sm">{industry?.title}</span>
                      </span>
                      <ArrowRight className="h-5 w-5 shrink-0 text-[#8aa0b4] transition group-hover:text-[#2d7d8e]" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <ul className="mt-6 space-y-1" aria-label="Industries">
            {industries.map((industry) => {
              const visual = industryVisuals[industry.id] ?? industryVisuals.other;
              return (
                <li key={industry.id}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => onSelect(industry.id)}
                    aria-label={`${industry.menu_label}. ${industry.title}. ${industry.status === "active" ? "Available" : "Coming soon"}.`}
                    className="group flex min-h-[4.75rem] w-full items-center gap-3 rounded-2xl px-2.5 py-2.5 text-left transition hover:bg-[#f3f7f8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7d8e] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 sm:gap-4 sm:px-3"
                  >
                    <span
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl shadow-[inset_0_0_0_1px_rgba(255,255,255,0.65)]"
                      style={{ backgroundColor: visual.background }}
                      aria-hidden="true"
                    >
                      {visual.emoji}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-bold leading-5 text-[#111827]">{industry.menu_label}</span>
                      <span className="mt-1 block text-xs leading-4 text-[#6b7d91] sm:text-sm">{industry.title}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {industry.status !== "active" && (
                        <span className="text-[0.58rem] font-bold uppercase tracking-[0.1em] text-[#8393a5] sm:text-[0.62rem] sm:tracking-[0.12em]">
                          Soon
                        </span>
                      )}
                      <span
                        className="h-5 w-5 rounded-full border border-[#c3d2df] bg-white transition group-hover:border-[#2d7d8e] group-hover:shadow-[inset_0_0_0_4px_white,0_0_0_1px_#2d7d8e]"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {saving && (
          <p className="mt-5 flex items-center justify-center gap-2 text-sm text-[#63748b]" aria-live="polite">
            <Loader2 className="h-4 w-4 animate-spin" /> Opening questionnaire
          </p>
        )}
        {error && <p className="mt-5 text-center text-sm text-destructive" role="alert">{error}</p>}
      </section>
    </main>
  );
}

function UnsupportedOutcomeScreen({
  desiredOutcome,
  onTryAnother,
  onBrowseExpertise,
}: {
  desiredOutcome: string;
  onTryAnother: () => void;
  onBrowseExpertise: () => void;
}) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#061511] px-4 py-6 sm:px-6 sm:py-10">
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          backgroundImage: [
            "radial-gradient(circle at 18% 8%, rgba(45,125,142,0.22), transparent 24rem)",
            "radial-gradient(circle at 86% 88%, rgba(31,78,121,0.2), transparent 28rem)",
            "repeating-linear-gradient(to bottom, transparent 0, transparent 48px, rgba(45,125,142,0.13) 48px, rgba(45,125,142,0.13) 76px)",
          ].join(","),
        }}
      />

      <section
        className="relative mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-2xl items-center sm:min-h-[calc(100vh-5rem)]"
        aria-labelledby="unsupported-outcome-title"
      >
        <div className="w-full rounded-[2rem] bg-white px-5 py-7 shadow-[0_28px_90px_-32px_rgba(0,0,0,0.9)] sm:rounded-[2.5rem] sm:px-10 sm:py-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#e8f3f5] text-[#2d7d8e]" aria-hidden="true">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-[#2d7d8e]">Unsupported outcome</p>
          <h1
            id="unsupported-outcome-title"
            className="mt-3 max-w-xl text-3xl font-extrabold leading-[1.08] tracking-[-0.035em] text-[#111827] sm:text-4xl"
          >
            {WIZARD_UNSUPPORTED_OUTCOME_TITLE}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-6 text-[#63748b] sm:text-base">
            {WIZARD_UNSUPPORTED_OUTCOME_DESCRIPTION}
          </p>
          <div className="mt-6 rounded-2xl bg-[#f2f7f8] px-4 py-3">
            <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-[#6b8192]">Submitted outcome</p>
            <p className="mt-1 text-sm font-semibold leading-5 text-[#173044]">{desiredOutcome}</p>
          </div>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              size="lg"
              onClick={onTryAnother}
              className="min-h-14 flex-1 rounded-2xl px-6 text-base font-bold"
            >
              {WIZARD_UNSUPPORTED_OUTCOME_PRIMARY_CTA}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              type="button"
              size="lg"
              variant="outline"
              onClick={onBrowseExpertise}
              className="min-h-14 flex-1 rounded-2xl px-6 text-base font-bold"
            >
              {WIZARD_UNSUPPORTED_OUTCOME_SECONDARY_CTA}
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}

function ReviewEditConfirmDialog({
  edit,
  onBack,
  onContinue,
  onAnswerOnlyThis,
}: {
  edit: PendingReviewEdit | null;
  onBack: () => void;
  onContinue: () => void;
  onAnswerOnlyThis: () => void;
}) {
  return (
    <Dialog open={!!edit} onOpenChange={(open) => { if (!open) onBack(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] rounded-2xl p-5 sm:max-w-md sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold leading-7 text-[#17283a]">
            Changing this answer will affect {edit?.affectedLabel ?? "later questions"}.
          </DialogTitle>
          <DialogDescription className="text-sm leading-6 text-[#63748b]">
            We'll quickly re-check them, then return you to review.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-2 sm:space-x-0">
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button type="button" variant="outline" onClick={onAnswerOnlyThis}>
            Answer only this
          </Button>
          <Button type="button" onClick={onContinue}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlueprintReviewScreen({
  run,
  saving,
  error,
  authenticated,
  onContinue,
  onEditAnswer,
  onChangeIndustry,
}: {
  run: WizardRun;
  saving: boolean;
  error: string;
  authenticated: boolean;
  onContinue: () => void;
  onEditAnswer: (questionId: string) => void;
  onChangeIndustry: () => void;
}) {
  const answerTrace = run.result?.answer_trace ?? run.questions.map((question) => {
    const storedAnswer = run.answers[question.question_id];
    const selected = question.options.find((option) => option.answer_id === storedAnswer);
    const section = run.questionnaire?.sections.find((item) => item.id === question.section_id);
    return {
      section_id: question.section_id,
      section_label: section?.label ?? question.section_id,
      question_id: question.question_id,
      prompt: question.prompt,
      answer_id: selected?.answer_id ?? null,
      answer_label: getCustomRouterAnswerText(storedAnswer) ?? selected?.label ?? "Not collected",
      score_values: selected?.score_values ?? {},
    };
  });
  const visual = run.industry ? industryVisuals[run.industry.id] ?? industryVisuals.other : industryVisuals.other;
  const lensName = run.result?.primary_lens?.name ?? run.result?.primary_lens_topic_id ?? "Selected expert method";
  const lensAuthor = run.result?.primary_lens?.author?.trim();
  const contextSummary = getWizardReviewContextSummary(run.result);
  const methodExplanation = run.result?.user_explanation?.trim();
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#061511] px-4 py-6 sm:px-6 sm:py-10">
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          backgroundImage: [
            "radial-gradient(circle at 12% 8%, rgba(45,125,142,0.24), transparent 25rem)",
            "radial-gradient(circle at 88% 88%, rgba(31,78,121,0.22), transparent 30rem)",
            "repeating-linear-gradient(to bottom, transparent 0, transparent 48px, rgba(45,125,142,0.12) 48px, rgba(45,125,142,0.12) 76px)",
          ].join(","),
        }}
      />

      <section className="relative mx-auto w-full max-w-4xl rounded-[2rem] bg-white px-5 py-7 shadow-[0_28px_90px_-32px_rgba(0,0,0,0.9)] sm:rounded-[2.5rem] sm:px-10 sm:py-10">
        <div className="mx-auto max-w-3xl">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#e8f3f5] text-[#2d7d8e]">
            <ClipboardCheck className="h-6 w-6" />
          </div>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-[#2d7d8e]">Review your blueprint inputs</p>
          <h1 className="mt-3 text-3xl font-extrabold leading-[1.08] tracking-[-0.035em] text-[#111827] sm:text-4xl">
            Check everything before we build.
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-[#63748b] sm:text-base">
            These inputs and the selected expert method will shape your App blueprint. Edit anything that does not look right.
          </p>

          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-[#f2f7f8] px-4 py-4 sm:col-span-2">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-[#6b8192]">Desired outcome</p>
              <p className="mt-1.5 text-sm font-semibold leading-6 text-[#173044] sm:text-base">{run.desired_outcome}</p>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-[#e1e9ee] px-4 py-4">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl"
                style={{ backgroundColor: visual.background }}
                aria-hidden="true"
              >
                {visual.emoji}
              </span>
              <div className="min-w-0">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#7d8da0]">Industry</p>
                <p className="mt-1 truncate text-sm font-bold text-[#17283a]">{run.industry?.menu_label}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-[#dce9ec] bg-[#f5fafb] px-4 py-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#dceff2] text-[#2d7d8e]" aria-hidden="true">
                <Sparkles className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#668493]">Chosen expert method</p>
                <p className="mt-1 text-sm font-bold leading-5 text-[#173044]">{lensName}</p>
                {lensAuthor && (
                  <p className="mt-0.5 text-xs font-semibold leading-5 text-[#668493]">by {lensAuthor}</p>
                )}
              </div>
            </div>
          </div>

          {methodExplanation && <p className="text-sm leading-6 text-[#173044]">{methodExplanation}</p>}
          {contextSummary && (
            <div className="mt-5 rounded-2xl border border-[#dce9ec] bg-[#f8fbfc] px-4 py-4">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#668493]">Collected context summary</p>
              <p className="mt-2 text-sm font-semibold leading-6 text-[#173044] sm:text-base">{contextSummary}</p>
            </div>
          )}

          <div className="mt-9 space-y-8">
            {run.questionnaire?.sections.map((section) => {
              const sectionAnswers = answerTrace.filter((answer) => answer.section_id === section.id);
              if (!sectionAnswers.length) return null;
              return (
                <section key={section.id} aria-labelledby={`review-section-${section.id}`}>
                  <div className="flex items-center justify-between gap-3 border-b border-[#e3eaef] pb-3">
                    <h2 id={`review-section-${section.id}`} className="text-lg font-extrabold text-[#17283a]">
                      {section.label}
                    </h2>
                    <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#8393a5]">
                      {sectionAnswers.length} answers
                    </span>
                  </div>
                  <dl className="divide-y divide-[#edf1f4]">
                    {sectionAnswers.map((answer, index) => (
                      <div key={answer.question_id} className="grid gap-3 py-4 sm:grid-cols-[2rem_minmax(0,1fr)_auto] sm:items-start">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#edf5f6] text-xs font-extrabold text-[#2d7d8e]" aria-hidden="true">
                          {index + 1}
                        </span>
                        <div>
                          <dt className="text-sm font-semibold leading-5 text-[#63748b]">{answer.prompt}</dt>
                          <dd className="mt-1.5 text-base font-bold leading-6 text-[#17283a]">{answer.answer_label}</dd>
                        </div>
                        <button
                          type="button"
                          onClick={() => onEditAnswer(answer.question_id)}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#dbe5eb] px-3 text-sm font-bold text-[#31546c] transition hover:border-[#2d7d8e] hover:bg-[#f1f7f8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7d8e] focus-visible:ring-offset-2 sm:min-h-9"
                          aria-label={`Edit answer for: ${answer.prompt}`}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                      </div>
                    ))}
                  </dl>
                </section>
              );
            })}
          </div>

          <p className="mt-7 rounded-2xl bg-[#f7f9fb] px-4 py-3 text-xs leading-5 text-[#718398]">
            For earlier answers, choose whether to re-check the affected questions or update only that answer.
          </p>

          {error && <p className="mt-5 text-sm text-destructive" role="alert">{error}</p>}
          <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button type="button" variant="ghost" disabled={saving} onClick={onChangeIndustry}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Change industry
            </Button>
            <Button
              type="button"
              size="lg"
              disabled={saving}
              onClick={onContinue}
              className="min-h-14 rounded-2xl px-6 text-base font-bold"
            >
              {saving ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Check className="mr-2 h-5 w-5" />}
              {authenticated ? "Create my App" : "Continue to App setup"}
              {!saving && <ArrowRight className="ml-2 h-5 w-5" />}
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}

function AccessFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#050d1f] px-4 py-6 text-[#122140] sm:px-6 sm:py-8">
      <div
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          backgroundImage: [
            "radial-gradient(circle at 12% 18%, rgba(45,125,142,0.28), transparent 26rem)",
            "radial-gradient(circle at 88% 82%, rgba(31,78,121,0.28), transparent 30rem)",
          ].join(","),
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:48px_48px] opacity-40" />
      <div className="relative mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-6xl flex-col sm:min-h-[calc(100vh-4rem)]">
        <header className="flex items-center justify-between gap-4 px-1 pb-6 sm:pb-8">
          <a href="/" className="inline-flex items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#79c4d0]">
            <img src={logo} alt="Applyt" className="h-10 w-auto brightness-0 invert sm:h-12" />
          </a>
          <p className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-white/55 sm:block">
            Managment Apps
          </p>
        </header>
        <div className="flex flex-1 items-center justify-center pb-4 sm:pb-8">
          {children}
        </div>
      </div>
    </main>
  );
}

function AccessCard({ children, step }: { children: React.ReactNode; step?: number }) {
  return (
    <section className="w-full max-w-3xl rounded-[2rem] border border-white/80 bg-white px-6 py-8 shadow-[0_30px_100px_-36px_rgba(0,0,0,0.8)] sm:rounded-[2.5rem] sm:px-12 sm:py-11 lg:px-14">
      {step && <AccessProgress currentStep={step} />}
      {children}
    </section>
  );
}

function AccessProgress({ currentStep }: { currentStep: number }) {
  return (
    <div className="mb-8 sm:mb-10">
      <div className="mb-3 flex items-center justify-between text-xs font-bold uppercase tracking-[0.18em]">
        <span className="text-[#2d7d8e]">Account setup</span>
        <span className="text-[#91a1b8]">Step {currentStep} of 4</span>
      </div>
      <div className="grid grid-cols-4 gap-2" aria-label={`Account setup step ${currentStep} of 4`}>
        {[1, 2, 3, 4].map((step) => (
          <span
            key={step}
            className={cn("h-1.5 rounded-full transition-colors", step <= currentStep ? "bg-[#2d7d8e]" : "bg-[#e6edf4]")}
          />
        ))}
      </div>
    </div>
  );
}

function AccessEyebrow({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#2d7d8e]">
      {icon}
      {children}
    </p>
  );
}

function WizardPrimaryButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "flex min-h-16 w-full items-center justify-center gap-3 rounded-2xl bg-[#122140] px-5 text-lg font-bold text-white shadow-lg shadow-[#122140]/15 transition hover:bg-[#19305d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7d8e] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:text-xl",
        className,
      )}
    >
      {children}
    </button>
  );
}

function AccessInput({
  id,
  label,
  icon,
  value,
  onChange,
  placeholder,
  type = "text",
  autoComplete,
  autoFocus,
  trailing,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-bold text-[#25456f]">{label}</label>
      <div className="flex min-h-16 items-center gap-4 rounded-2xl border border-[#dce4ee] bg-[#f8fafd] px-5 text-[#8da0b8] transition focus-within:border-[#79aeb9] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#2d7d8e]/10">
        <span className="shrink-0" aria-hidden>{icon}</span>
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className="h-auto border-none bg-transparent p-0 text-lg text-[#122140] shadow-none placeholder:text-[#9aa6b8] focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-xl"
        />
        {trailing}
      </div>
    </div>
  );
}

function AccountEmail({ accountEmail, onChange }: { accountEmail: string; onChange: () => void }) {
  const maskedEmail = maskAccountEmail(accountEmail);

  return (
    <div className="mt-7 flex min-h-14 items-center gap-3 rounded-2xl border border-[#dce4ee] bg-[#f8fafd] px-4 text-sm text-[#6f84a3] sm:px-5 sm:text-base">
      <Mail className="h-5 w-5 shrink-0 text-[#2d7d8e]" />
      <span className="min-w-0 flex-1 truncate" aria-label="Selected email address hidden for privacy">{maskedEmail}</span>
      <button type="button" className="font-bold text-[#2d6b7e] transition hover:text-[#122140]" onClick={onChange}>
        Change
      </button>
    </div>
  );
}

function AccessError({ message }: { message: string }) {
  return message ? <p className="text-sm font-medium text-destructive" role="alert">{message}</p> : null;
}

function TrustCallout() {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-[#c9dde2] bg-[#eff7f8] px-4 py-4 text-[#2d6b7e] sm:px-5">
      <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
      <p className="text-sm font-semibold leading-6 sm:text-base">
        Your answers stay linked to this route while Applyt prepares your workspace.
      </p>
    </div>
  );
}

function LegalAndLogin({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="mt-8 space-y-5 text-center text-sm leading-6 text-[#8a9ab3]">
      <p>
        By clicking Continue, you agree to our{" "}
        <a className="font-medium text-[#2d6b7e] underline underline-offset-4" href="/terms">Terms of Service</a>{" "}
        and{" "}
        <a className="font-medium text-[#2d6b7e] underline underline-offset-4" href="/privacy">Privacy Policy</a>.
      </p>
      <ExistingAccountLoginAction onLogin={onLogin} className="mt-0" />
    </div>
  );
}

function ExistingAccountLoginAction({ onLogin, className }: { onLogin: () => void; className?: string }) {
  return (
    <p className={cn("mt-8 text-center text-sm leading-6 text-[#8a9ab3]", className)}>
      Already have an account?{" "}
      <button type="button" className="font-bold text-[#2d6b7e] underline underline-offset-4" onClick={onLogin}>
        Log in
      </button>
    </p>
  );
}

function VerificationPendingCard({
  email,
  error,
  saving,
  verificationReady,
  onContinue,
}: {
  email: string;
  error: string;
  saving: boolean;
  verificationReady: boolean;
  onContinue: () => void;
}) {
  const isVerifying = verificationReady && saving;
  return (
    <AccessCard>
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eff7f8] text-[#2d7d8e]">
        <Mail className="h-8 w-8" />
      </div>
      <div className="mt-7 text-center">
        <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#2d6b7e]">Email verification</p>
        <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] text-[#122140] sm:text-5xl">
          {verificationReady ? (
            <>Verifying your email<br /><span className="text-[#2d7d8e]">and starting the build.</span></>
          ) : "Check your email"}
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-lg leading-8 text-[#6f84a3] sm:text-xl">
          {isVerifying
            ? "Keep this tab open. Applyt is confirming your email and preparing your App build."
            : verificationReady
              ? "Applyt will verify your email and start your App build automatically in this window."
              : "Open the verification link we sent to your email address. Your App build will start automatically in the window that opens."}
        </p>
      </div>
      {email && (
        <div className="mt-7 flex min-h-14 items-center gap-3 rounded-2xl border border-[#dce4ee] bg-[#f8fafd] px-4 text-sm text-[#6f84a3] sm:px-5 sm:text-base">
          <Mail className="h-5 w-5 shrink-0 text-[#2d7d8e]" />
          <span className="min-w-0 flex-1 truncate" aria-label="Verification email address hidden for privacy">{maskAccountEmail(email)}</span>
        </div>
      )}
      <div className="mt-8 rounded-2xl border border-[#c9dde2] bg-[#eff7f8] px-4 py-4 text-[#2d6b7e] sm:px-5">
        <p className="flex items-start gap-3 text-sm font-semibold leading-6 sm:text-base">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          Your access is saved. Your App build starts only after this email is verified.
        </p>
      </div>
      <AccessError message={error} />
      {!verificationReady && (
        <p className="mt-7 text-center text-lg text-[#6f84a3]">You can close this page.</p>
      )}
      {verificationReady && error && <button
        type="button"
        disabled={saving}
        onClick={onContinue}
        className="mt-7 flex min-h-16 w-full items-center justify-center gap-3 rounded-2xl bg-[#122140] px-5 text-lg font-bold text-white shadow-lg shadow-[#122140]/15 transition hover:bg-[#19305d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7d8e] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:text-xl"
      >
        {saving && <Loader2 className="h-5 w-5 animate-spin" />}
        {isVerifying ? "Verifying and starting build" : "Retry email verification"}
        <ArrowRight className="h-6 w-6" />
      </button>}
    </AccessCard>
  );
}

function CheckoutCard({
  account,
  mode,
  error,
  saving,
  onModeChange,
  onAccountChange,
  onSubmit,
  onLogin,
}: {
  account: WizardAccount;
  mode: PaymentMode;
  error: string;
  saving: boolean;
  onModeChange: (mode: PaymentMode) => void;
  onAccountChange: (account: WizardAccount) => void;
  onSubmit: () => void;
  onLogin: () => void;
}) {
  const promoReady = mode === "promo" && account.promoCode.trim().length > 0;

  const submitCheckout = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mode !== "promo" || !promoReady || saving) return;
    onSubmit();
  };

  return (
    <AccessCard>
      <div className="text-center">
        <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#2d6b7e]"><Lock className="mr-2 inline h-4 w-4" /> Secure checkout</p>
        <h1 className="mt-6 font-serif text-4xl font-bold leading-tight text-[#122140] sm:text-5xl">Unlock your App</h1>
        <p className="mx-auto mt-5 max-w-xl text-xl leading-8 text-[#7892b4]">Your personalised App and AI actions, built for your exact outcome.</p>
      </div>

      <div className="mx-auto mt-10 max-w-2xl rounded-3xl border border-[#cdd8e5] bg-[#f4f7fa] p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div>
            <h2 className="text-2xl font-bold text-[#122140]">Applyt Full Access</h2>
            <p className="mt-2 text-lg text-[#7892b4]">One-time · App + Blueprint + AI Actions</p>
          </div>
          <div className="sm:text-right">
            <p className="text-5xl font-bold text-[#122140]">$49</p>
            <p className="text-sm text-[#7892b4]">one-time</p>
          </div>
        </div>
        <div className="mt-7 space-y-4 text-xl font-medium text-[#25456f]">
          {["Personalised App (5 modules)", "Strategic AI-generated modules", "Live App dashboard", "6 AI actions ready to run"].map((item) => (
            <p key={item} className="flex items-center gap-3"><Check className="h-5 w-5 text-[#2d6b7e]" /> {item}</p>
          ))}
        </div>
      </div>

      <form className="mx-auto mt-9 max-w-2xl space-y-6" onSubmit={submitCheckout}>
        <Tabs value={mode} onValueChange={(value) => onModeChange(value as PaymentMode)}>
          <TabsList className="grid h-12 w-full grid-cols-2 rounded-2xl">
            <TabsTrigger value="promo" className="rounded-xl">Promo code</TabsTrigger>
            <TabsTrigger value="card" className="rounded-xl">Card</TabsTrigger>
          </TabsList>
          <TabsContent value="promo" className="mt-6 space-y-5">
            <CheckoutField
              icon={<ShieldCheck className="h-5 w-5" />}
              value={account.promoCode}
              onChange={(value) => onAccountChange({ ...account, promoCode: value })}
              placeholder="APPLYT-..."
              label="Promo code"
            />
            <p className="text-sm leading-6 text-[#7892b4]">
              After promo access is approved, we send a verification email. Your App build starts after you verify.
            </p>
          </TabsContent>
          <TabsContent value="card" className="mt-6 space-y-5">
            <CheckoutField
              icon={<CreditCard className="h-5 w-5" />}
              value={account.cardNumber}
              onChange={(value) => onAccountChange({ ...account, cardNumber: value })}
              placeholder="1234 5678 9012 3456"
              label="Card number"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <CheckoutField value={account.expiry} onChange={(value) => onAccountChange({ ...account, expiry: value })} placeholder="MM / YY" label="Expiry date" />
              <CheckoutField value={account.cvc} onChange={(value) => onAccountChange({ ...account, cvc: value })} placeholder="..." label="CVC" />
            </div>
            <CheckoutField value={account.cardName} onChange={(value) => onAccountChange({ ...account, cardName: value })} placeholder="Full name" label="Name on card" />
            <p className="text-sm leading-6 text-[#7892b4]">
              Card checkout is not connected in DEV yet. When enabled, verification email will be sent only after payment succeeds.
            </p>
          </TabsContent>
        </Tabs>

        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={saving || mode === "card" || !promoReady}
          className="flex min-h-16 w-full items-center justify-center gap-3 rounded-3xl bg-[#122140] px-5 text-xl font-bold text-white shadow-lg shadow-[#122140]/15 transition hover:bg-[#19305d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
          {mode === "promo" ? "Use promo & verify email" : "Pay $49 & verify email"}
          <ArrowRight className="h-6 w-6" />
        </button>
      </form>
      <ExistingAccountLoginAction onLogin={onLogin} />
      <p className="mt-6 text-center text-sm text-[#91a1bb]">Powered by Stripe · 256-bit SSL · Money-back guarantee</p>
    </AccessCard>
  );
}

function CheckoutField({
  icon,
  value,
  onChange,
  placeholder,
  label,
}: {
  icon?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-[#25456f]">{label}</span>
      <span className="flex min-h-14 items-center gap-3 rounded-2xl border border-[#dce4ee] bg-[#f8fafd] px-5 text-[#93a2b8]">
        {icon}
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="h-auto border-none bg-transparent p-0 text-lg text-[#122140] shadow-none placeholder:text-[#91a1bb] focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </span>
    </label>
  );
}

function RoutingProgressScreen({
  progress,
  lens,
  desiredOutcome,
}: {
  progress: number;
  lens: ResolvedLens | null;
  desiredOutcome: string;
}) {
  const message = routingMessages.find((item) => progress <= item.maxProgress) ?? routingMessages[routingMessages.length - 1];
  const lensName = lens?.name || "Your best-fit Expert Lens";
  const showLens = progress > 34;
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black px-6 text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,79,122,0.22),transparent_32rem)]" />
      <div className="relative w-full max-w-3xl text-center">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/45">Your Applyt route</p>
        <div className="relative mx-auto mt-7 flex h-16 w-16 items-center justify-center">
          <span className="absolute h-16 w-16 animate-ping rounded-full border border-[#2d7d8e]/40" />
          <span className="absolute h-12 w-12 animate-pulse rounded-full border border-[#5bb8c7]/50 bg-[#2d7d8e]/10" />
          <span className="h-3 w-3 rounded-full bg-[#79c4d0] shadow-[0_0_24px_rgba(121,196,208,0.85)]" />
        </div>
        <h1 className="mt-7 text-3xl font-bold leading-tight sm:text-5xl">
          {message.title}
          <span className="ml-1 inline-block h-[0.9em] w-0.5 translate-y-1 animate-pulse bg-[#79c4d0]" aria-hidden />
        </h1>
        <div className={cn("mx-auto mt-7 max-w-2xl rounded-3xl border border-[#79c4d0]/30 bg-white/[0.07] px-5 py-6 text-left shadow-[0_20px_70px_-30px_rgba(45,125,142,0.8)] backdrop-blur-sm transition duration-700 sm:px-8 sm:py-7", showLens ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0")}>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#79c4d0]">
            <Sparkles className="h-4 w-4" /> Selected Expert Lens
          </p>
          <h2 className="mt-3 font-serif text-3xl font-bold leading-tight text-white sm:text-4xl">{lensName}</h2>
          <p className="mt-3 text-sm leading-6 text-white/60 sm:text-base">
            Selected deterministically from your questionnaire answers for{" "}
            <span className="font-semibold text-white/85">{desiredOutcome}</span>.
          </p>
          <div className="mt-5 grid gap-2 text-sm font-medium text-white/70 sm:grid-cols-3">
            {["Expert operating logic", "Decision rules", "Execution path"].map((value) => (
              <span key={value} className="flex items-center gap-2 rounded-xl bg-white/[0.06] px-3 py-2">
                <Check className="h-4 w-4 shrink-0 text-[#79c4d0]" /> {value}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 w-[min(78vw,700px)] -translate-x-1/2">
        <div className="mb-4 flex items-center justify-between text-sm text-white/45 sm:text-base">
          <span>{message.label}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1 rounded-full bg-white/15">
          <div className="h-full rounded-full bg-[#79c4d0] transition-all duration-300" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
      </div>
    </main>
  );
}

function ArtifactStreamBackdrop({ text }: { text: string }) {
  const visibleText = tailArtifactText(text).trim();
  if (!visibleText) return null;

  return (
    <div className="absolute inset-0 overflow-hidden bg-black" aria-hidden>
      <div className="absolute inset-0 flex items-end overflow-hidden px-3 py-8 sm:px-5 sm:py-10">
        <pre className="w-full whitespace-pre-wrap break-words font-mono text-[11px] leading-[1.45] tracking-normal text-[#2dff2a]/55 sm:text-sm lg:text-base">
          {visibleText}
          <span className="ml-1 inline-block h-[1em] w-2 translate-y-0.5 animate-pulse bg-[#2dff2a]" />
        </pre>
      </div>
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black via-black/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black via-black/80 to-transparent" />
    </div>
  );
}

function BuildProgressScreen({ progress, artifactText }: { progress: number; artifactText: string }) {
  const message = buildMessages.find((item) => progress <= item.maxProgress) ?? buildMessages[buildMessages.length - 1];
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black px-6 text-white">
      <ArtifactStreamBackdrop text={artifactText} />
      <div className="relative z-10 max-w-3xl text-center" role="status" aria-live="polite" aria-atomic="true">
        <h1 className="text-4xl font-bold leading-tight sm:text-5xl">
          {message.title}
          <span className="ml-1 inline-block h-[0.9em] w-0.5 translate-y-1 animate-pulse bg-blue-500" aria-hidden />
        </h1>
      </div>
      <div className="absolute bottom-10 left-1/2 z-10 w-[min(70vw,700px)] -translate-x-1/2">
        <div className="flex items-center justify-center gap-3 text-base text-white/50">
          <span>{message.label}</span>
          <Loader2 className="h-5 w-5 animate-spin text-white/70" aria-hidden />
        </div>
      </div>
    </main>
  );
}

function StateMessage({ icon, title, description, children }: { icon?: React.ReactNode; title: string; description?: string; children?: React.ReactNode }) {
  return <Card className="mx-auto max-w-lg"><CardContent className="p-8 text-center">{icon && <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">{icon}</div>}<h1 className="text-xl font-semibold">{title}</h1>{description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}{children && <div className="mt-5">{children}</div>}</CardContent></Card>;
}
